import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { DelayedPageLoader } from '../ui/PageLoader';

// The pending gate must keep the invitation-accept surface reachable, or the
// join loop deadlocks (invite email → bounced to /pending → can never accept).
const PENDING_GATE_ALLOWED_PATHS = ['/pending', '/accept-invites'];

export function ProtectedRoute() {
  const {
    isAuthenticated,
    isLoading,
    user,
    joinRequest,
    joinRequestLookupFailed,
  } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <DelayedPageLoader />;
  }

  if (!isAuthenticated) {
    // Redirect to the login page, but save the current location they were
    // trying to go to so we can send them there after logging in
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if email verification is required. Consume the server-computed
  // predicate (Project C PR2) — NOT `emailVerified === false` — so a fresh
  // unmatched-wizard user in their grace window is NOT bounced (the API admits
  // them; a naive false-check here would destroy that grace UX).
  if (
    user?.emailVerificationRequired &&
    location.pathname !== '/verify-email'
  ) {
    // Redirect to email verification page, but preserve the intended destination
    return <Navigate to="/verify-email" replace />;
  }

  // Pending gate (Project A): a verified user with zero clinics and an OPEN
  // join request decides join-or-create on /pending. Keying on the row keeps
  // legacy zero-clinic users (joinRequest === null) untouched. A FAILED lookup
  // gates too (fail safe): a matched pending user with a transient error
  // belongs on /pending's retry UI, not in an empty clinic-less app.
  if (
    user &&
    !user.emailVerificationRequired &&
    user.clinicIds?.length === 0 &&
    ((joinRequest && joinRequest.closedAt === null) ||
      joinRequestLookupFailed) &&
    !PENDING_GATE_ALLOWED_PATHS.includes(location.pathname)
  ) {
    return <Navigate to="/pending" replace />;
  }

  // If authenticated and email verified (or not required), render the child routes
  return <Outlet />;
}
